import React, { useCallback, useMemo, useState } from "react";
import { TimeSettings, useTimeSettings } from "@/state/time-settings";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  DialogPage,
  MultiStepDialogContent,
} from "../paginated-dialog-content/multi-step-dialog-content";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { ServiceName, ServiceNames } from "./ServiceNameType";
import { BulkServiceVersionSelectionTableWithSelection } from "./bulk-service-version-selection-table-with-selection";
import {
  RunImmediatelyInput,
  ScheduleSpecificInput,
} from "./run-runImmediately-input";
import { cn } from "@/lib/utils";
import { TzPicker } from "@/routes/dashboard/-tz-picker";
import { FormDatePickerItem } from "../form-inputs/date-picker";
import { ScheduleJobScheduleDto } from "@/client";
import {
  AgentDowngradeTarget,
  AgentDowngradeWarning,
} from "./agent-downgrade-warning";
import {
  findDowngradedTargets,
  useAgentDowngradeConfirmation,
} from "./agent-downgrade-targets";

const deploymentSchema = z.object({
  version: z.string().min(1),
  type: z.string().optional(),
});

export const BulkScheduleDeploymentSchema = z
  .object({
    serviceName: z.enum(ServiceNames),
    deployment: deploymentSchema,
    runImmediately: z.boolean(),
    schedule: z.date().optional(),
    timezone: z
      .string()
      .min(1, { message: "Please select a timezone" })
      .optional(),
  })
  .refine(
    (data) => {
      if (data.runImmediately) {
        return true;
      }
      return data.schedule !== undefined;
    },
    {
      message: "Please select a schedule",
      path: ["schedule"],
    },
  )
  .refine(
    (data) => {
      if (data.runImmediately) {
        return true;
      }
      return data.timezone && data.timezone.length > 0;
    },
    {
      message: "Please select a timezone",
      path: ["timezone"],
    },
  )
  .refine(
    (data) => {
      if (data.runImmediately) {
        return true;
      }
      if (!data.schedule) {
        return true;
      }
      return data.schedule > new Date();
    },
    {
      message:
        'Cannot schedule in the past. Use "Run Immediately" to deploy now.',
      path: ["schedule"],
    },
  );

// Helper function to get component display name for translation
function getComponentDisplayName(serviceName: ServiceName): string {
  switch (serviceName) {
    case "edgify-agent":
      return "agent";
    case "edgify-non-scan-detector":
      return "nonScanDetector";
    case "edgify-on-screen-app":
      return "onScreenApp";
    case "edgify-zebra-adapter":
      return "zebraAdapter";
    case "edgify-worker":
      return "trainer";
    default:
      return serviceName;
  }
}

// Helper function to get service display name for UI
function getServiceDisplayName(serviceName: ServiceName): string {
  switch (serviceName) {
    case "edgify-agent":
      return "Edgify Agent";
    case "edgify-non-scan-detector":
      return "Non Scan Detector";
    case "edgify-on-screen-app":
      return "On-Screen App";
    case "edgify-zebra-adapter":
      return "Zebra Adapter";
    case "edgify-worker":
      return "Trainer";
    default:
      return serviceName;
  }
}

interface BulkScheduleDeploymentProps {
  apiCallback: (
    serviceName: ServiceName,
    version: string,
    type?: string,
    schedule?: ScheduleJobScheduleDto,
  ) => Promise<any>;
  onClose?: () => void;
  excludeServices?: ServiceName[];
  resourceType: "device" | "site";
  // The selected targets that can break on an agent downgrade, with their
  // current agent version; undefined when they are not all known.
  agentTargets?: AgentDowngradeTarget[];
}

const VERSION_STEP = 1;

export const BulkScheduleDeployment = ({
  apiCallback: _apiCallback,
  onClose,
  excludeServices = [],
  resourceType,
  agentTargets,
}: BulkScheduleDeploymentProps) => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);

  const availableServices = ServiceNames.filter(
    (service) => !excludeServices.includes(service),
  );

  const timeSettings = useTimeSettings();

  const form = useForm<z.infer<typeof BulkScheduleDeploymentSchema>>({
    resolver: zodResolver(BulkScheduleDeploymentSchema),
    defaultValues: {
      serviceName: availableServices[0],
      runImmediately: true,
      timezone: timeSettings.timeZone,
      schedule: new Date(),
    },
  });

  const serviceName = form.watch("serviceName");
  const runImmediately = form.watch("runImmediately");
  const timezone = form.watch("timezone");
  const schedule = form.watch("schedule");
  const deployment = form.watch("deployment");

  const downgradedTargets = useMemo(() => {
    if (serviceName !== "edgify-agent" || !deployment?.version) return [];
    return findDowngradedTargets(deployment.version, agentTargets);
  }, [serviceName, deployment?.version, agentTargets]);
  // Unknown targets may run a newer agent, so they are warned too.
  const confirmation = useAgentDowngradeConfirmation(
    downgradedTargets,
    `${serviceName}:${deployment?.version}`,
  );

  const apiCallback = useCallback(
    ({
      version,
      type,
      schedule,
    }: {
      version: string;
      type?: string;
      schedule?: ScheduleJobScheduleDto;
    }) => {
      return _apiCallback(serviceName, version, type, schedule);
    },
    [_apiCallback, serviceName],
  );

  const onSubmit = (data: z.infer<typeof BulkScheduleDeploymentSchema>) => {
    // A risk found after the warning step was passed sends the user back to it.
    if (!confirmation.isConfirmed) {
      confirmation.capture();
      setCurrentStep(VERSION_STEP + 1);
      return;
    }
    if (data.runImmediately) {
      apiCallback({
        version: data.deployment.version,
        type: data.deployment.type,
      }).then(() => {
        onClose?.();
      });
    } else {
      const schedule: ScheduleJobScheduleDto = {
        name: "Scheduled Bulk Device Deployment",
        startDate: data.schedule!,
        timezone: data.timezone!,
      };
      apiCallback({
        version: data.deployment.version,
        type: data.deployment.type,
        schedule,
      }).then(() => {
        onClose?.();
      });
    }
  };

  // Page 0: Component Selection
  const Page0 = useMemo(
    () => (
      <div className="space-y-4">
        <h1 className="text-lg font-semibold mb-4">
          {t("chooseTheComponentYouWantToDeploy")}
        </h1>
        <FormField
          control={form.control}
          name="serviceName"
          render={({ field }) => (
            <FormItem className="py-4">
              <FormControl>
                <div className="space-y-2">
                  {availableServices.map((service) => (
                    <div
                      key={service}
                      className={cn(
                        "border p-4 rounded-md cursor-pointer transition-colors hover:bg-ghost",
                        field.value === service &&
                          "border-azure bg-azure/10 hover:bg-azure/15",
                      )}
                      onClick={() => field.onChange(service)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="font-medium">
                          {getServiceDisplayName(service)}
                        </div>
                        {field.value === service && (
                          <div className="text-azure">✓</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </FormControl>
            </FormItem>
          )}
        />
      </div>
    ),
    [form.control, t, availableServices],
  );

  // Page 1: Version Selection
  const Page1 = useMemo(
    () => (
      <div className="space-y-4">
        <h1>{`${t("select")} ${t(getComponentDisplayName(serviceName))} ${t("version")}`}</h1>
        <FormField
          control={form.control}
          name="deployment"
          render={({ field }) => (
            <FormItem className="py-4">
              <FormLabel>{`${t("chooseThe")} ${t(getComponentDisplayName(serviceName))} ${t("version")} ${t("youWantToDeployTo")} ${t(resourceType === "device" ? "devices" : "sites")}`}</FormLabel>
              <FormControl>
                <BulkServiceVersionSelectionTableWithSelection
                  componentName={serviceName}
                  resourceType={resourceType}
                  onSelect={(value) => {
                    if (value) {
                      field.onChange({
                        version: value.version,
                        type: value.type,
                      });
                    } else {
                      field.onChange(undefined);
                    }
                  }}
                />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
    ),
    [form.control, t, serviceName, resourceType],
  );

  // Page 2: Schedule Selection
  const Page2 = useMemo(() => {
    return (
      <div className="space-y-4">
        <h1>{`${t("chooseWhenYouWantToDeployThe")} ${t(getComponentDisplayName(serviceName))} ${t("to")} ${t("devices")}`}</h1>
        <div
          className={cn("border p-2 rounded-md mb-2", {
            "border-azure bg-azure/10": runImmediately,
          })}
        >
          <FormField
            control={form.control}
            name="runImmediately"
            render={({ field }) => (
              <FormItem className="">
                <FormControl>
                  <RunImmediatelyInput
                    {...field}
                    description={t("deployComponentRightAway")}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <div
          className={cn("border p-2 rounded-md mb-2", {
            "border-azure bg-azure/10": !runImmediately,
          })}
        >
          <FormField
            control={form.control}
            name="runImmediately"
            render={({ field }) => (
              <FormItem className="">
                <FormControl>
                  <ScheduleSpecificInput {...field} />
                </FormControl>
              </FormItem>
            )}
          />
          <div className={"flex  flex-col gap-2 ml-5 mt-4"}>
            <FormDatePickerItem
              {...timeSettings.as(timezone)}
              name="schedule"
              form={form}
              label={t("date")}
              button={{ disabled: runImmediately }}
              container={{ className: runImmediately ? "opacity-50" : "" }}
            />
            <FormField
              key={`timezone-${currentStep}`}
              control={form.control}
              name="timezone"
              render={({ field }) => (
                <FormItem className={runImmediately ? "opacity-50" : ""}>
                  <FormLabel>{t("timezone")}</FormLabel>
                  <FormControl>
                    <TzPicker
                      className="w-full"
                      value={field.value as TimeSettings["timezone"]}
                      onChange={(tz) => field.onChange(tz)}
                      isDisabled={runImmediately}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </div>
      </div>
    );
  }, [form.control, t, runImmediately, currentStep]);

  // Page 3: Summary
  const Page3 = useMemo(() => {
    return (
      <div className="space-y-4">
        <div className="flex flex-col gap-2">
          <div className={cn("border p-2 rounded-md mb-2 bg-azure/5")}>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="">{t("component")}</div>
                <div className="">
                  {t(getComponentDisplayName(serviceName))} {t("version")}
                </div>
                {deployment?.type && (
                  <div className="">
                    {t(getComponentDisplayName(serviceName))} {t("type")}
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <div className="font-normal">
                  {getServiceDisplayName(serviceName)}
                </div>
                <div className="font-normal">{deployment?.version}</div>
                {deployment?.type && (
                  <div className="font-normal">{deployment.type}</div>
                )}
              </div>
            </div>
          </div>
          <div className={cn("border p-2 rounded-md mb-2 bg-azure/5")}>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="">{t("deploymentType")}</div>
                {!runImmediately && (
                  <>
                    <div className="">{t("scheduledDate")}</div>
                    <div className="">{t("scheduledTime")}</div>
                    <div className="">{t("timezone")}</div>
                  </>
                )}
              </div>
              <div className="space-y-2">
                <div className="font-normal">
                  {runImmediately ? t("immediate") : t("scheduled")}
                </div>
                {!runImmediately && (
                  <>
                    <div className="font-normal">
                      {schedule?.toLocaleDateString()}
                    </div>
                    <div className="font-normal">
                      {schedule?.toLocaleTimeString()}
                    </div>
                    <div className="font-normal">{timezone}</div>
                  </>
                )}
              </div>
            </div>
          </div>
          {!runImmediately && (
            <div
              className={cn(
                "border p-2 rounded-md mb-2 border-azure bg-azure/10 text-azure font-bold",
              )}
            >
              A Pending job will be created and the component will be deployed
              at the scheduled time.
            </div>
          )}
        </div>
      </div>
    );
  }, [form, t, runImmediately, timezone, schedule, deployment, serviceName]);

  const pages: DialogPage[] = useMemo(
    () =>
      [
        {
          title: t("selectComponent"),
          content: Page0,
          fields: ["serviceName"],
        },
        {
          title: t("selectVersion"),
          content: Page1,
          fields: ["deployment"],
        },
        confirmation.warning && {
          title: t("downgradeWarning"),
          content: (
            <AgentDowngradeWarning
              version={deployment?.version}
              targets={confirmation.warning?.targets}
              acknowledged={confirmation.acknowledged}
              onAcknowledgedChange={confirmation.setAcknowledged}
            />
          ),
          disableNext: !confirmation.acknowledged,
        },
        {
          title: t("scheduler"),
          content: Page2,
          fields: runImmediately ? [] : ["schedule", "timezone"],
        },
        {
          title: t("summary"),
          content: Page3,
        },
      ].filter(Boolean) as DialogPage[],
    [
      Page0,
      Page1,
      Page2,
      Page3,
      t,
      runImmediately,
      deployment?.version,
      confirmation.warning,
      confirmation.acknowledged,
      confirmation.setAcknowledged,
    ],
  );

  const handleNext = useCallback(async () => {
    const fields = pages[currentStep].fields;

    // Validate without focusing
    const isStepValid = await form.trigger(
      fields as (keyof z.infer<typeof BulkScheduleDeploymentSchema>)[],
      { shouldFocus: false },
    );

    if (isStepValid) {
      if (currentStep === VERSION_STEP) confirmation.capture();
      setCurrentStep((prev) => prev + 1);
    }
  }, [currentStep, pages, form, confirmation.capture]);

  const handleBack = useCallback(() => {
    setCurrentStep((prev) => prev - 1);
  }, []);

  return (
    <Form {...form}>
      <form className="flex flex-col flex-1 min-h-0">
        <MultiStepDialogContent
          pages={pages}
          currentStep={currentStep}
          onNext={handleNext}
          onBack={handleBack}
          onSubmit={form.handleSubmit(onSubmit)}
          finishButtonText={t("install")}
          className="min-w-[28rem]"
        />
      </form>
    </Form>
  );
};
